import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Alert, AlertDescription } from '../ui/alert';
import { 
  Search, 
  RefreshCw, 
  AlertTriangle, 
  Car, 
  Calendar, 
  MapPin,
  DollarSign,
  User,
  Eye,
  CheckCircle,
  Clock,
  Zap
} from 'lucide-react';
import { apiService } from '../../services/api';

export const MultasPage = () => {
  const [multas, setMultas] = useState([]);
  const [multasNovas, setMultasNovas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [consultando, setConsultando] = useState(false);
  const [placaConsulta, setPlacaConsulta] = useState('');

  useEffect(() => {
    carregarMultas();
  }, []);

  const carregarMultas = async () => {
    try {
      const [multasData, novasData] = await Promise.all([
        apiService.getMultas(),
        apiService.getMultasNovas()
      ]);
      
      setMultas(multasData);
      setMultasNovas(novasData);
    } catch (error) {
      console.error('Erro ao carregar multas:', error);
    } finally {
      setLoading(false);
    }
  };

  const consultarTodasMotos = async () => {
    setConsultando(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/multas/consultar-todas`, {
        method: 'POST'
      });
      
      if (response.ok) {
        await carregarMultas();
        alert('Consulta realizada com sucesso! Verifique as novas multas encontradas.');
      } else {
        alert('Erro ao consultar multas. Tente novamente.');
      }
    } catch (error) {
      console.error('Erro na consulta:', error);
      alert('Erro ao consultar multas. Tente novamente.');
    } finally {
      setConsultando(false);
    }
  };

  const consultarPlaca = async () => {
    if (!placaConsulta.trim()) {
      alert('Digite uma placa para consultar');
      return;
    }
    
    setConsultando(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/multas/consultar-manual/${placaConsulta}`, {
        method: 'POST'
      });
      
      const data = await response.json();
      
      if (response.ok) {
        await carregarMultas();
        alert(`Consulta concluída! ${data.multas_encontradas} multa(s) encontrada(s) para a placa ${placaConsulta}`);
        setPlacaConsulta('');
      } else {
        alert(data.detail || 'Erro ao consultar placa');
      }
    } catch (error) {
      console.error('Erro na consulta:', error);
      alert('Erro ao consultar placa. Tente novamente.');
    } finally {
      setConsultando(false);
    }
  };

  const marcarComoNotificada = async (multaId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/multas/${multaId}/notificar`, {
        method: 'PUT'
      });
      
      if (response.ok) {
        carregarMultas();
      }
    } catch (error) {
      console.error('Erro ao marcar multa:', error);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'nova': return 'bg-red-100 text-red-800';
      case 'notificada': return 'bg-yellow-100 text-yellow-800';
      case 'cobranca_criada': return 'bg-blue-100 text-blue-800';
      case 'paga': return 'bg-green-100 text-green-800';
      case 'contestada': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'nova': return 'Nova';
      case 'notificada': return 'Notificada';
      case 'cobranca_criada': return 'Cobrança Criada';
      case 'paga': return 'Paga';
      case 'contestada': return 'Contestada';
      default: return status;
    }
  };

  const getTipoColor = (tipo) => {
    switch (tipo) {
      case 'velocidade': return 'bg-red-100 text-red-800';
      case 'sinal_vermelho': return 'bg-red-100 text-red-800';
      case 'estacionamento': return 'bg-yellow-100 text-yellow-800';
      case 'capacete': return 'bg-orange-100 text-orange-800';
      case 'cnh_vencida': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getTipoText = (tipo) => {
    switch (tipo) {
      case 'velocidade': return 'Excesso de Velocidade';
      case 'sinal_vermelho': return 'Sinal Vermelho';
      case 'estacionamento': return 'Estacionamento';
      case 'capacete': return 'Sem Capacete';
      case 'cnh_vencida': return 'CNH Vencida';
      default: return tipo;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('pt-BR');
    } catch (error) {
      return 'N/A';
    }
  };

  const isVencimentoProximo = (dataVencimento) => {
    const hoje = new Date();
    const vencimento = new Date(dataVencimento);
    const diffDays = Math.ceil((vencimento - hoje) / (1000 * 60 * 60 * 24));
    return diffDays <= 7 && diffDays >= 0;
  };

  const isVencida = (dataVencimento) => {
    const hoje = new Date();
    const vencimento = new Date(dataVencimento);
    return vencimento < hoje;
  };

  const temDesconto = (multa) => {
    if (!multa.valor_desconto || !multa.data_vencimento_desconto) return false;
    const hoje = new Date();
    const vencimentoDesconto = new Date(multa.data_vencimento_desconto);
    return vencimentoDesconto >= hoje;
  };

  if (loading) {
    return <div className="flex justify-center p-8">Carregando multas...</div>;
  }

  const multasVencidas = multas.filter(m => isVencida(m.data_vencimento));
  const multasProximoVencimento = multas.filter(m => isVencimentoProximo(m.data_vencimento));
  const totalMultas = multas.reduce((acc, m) => acc + (m.valor_desconto && temDesconto(m) ? m.valor_desconto : m.valor_original), 0);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">
          Sistema de Monitoramento DETRAN-SP
        </h2>
        
        <div className="flex space-x-2">
          <Button 
            onClick={carregarMultas}
            variant="outline"
            disabled={consultando}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${consultando ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
          
          <Button 
            onClick={consultarTodasMotos}
            className="bg-blue-600 hover:bg-blue-700"
            disabled={consultando}
          >
            <Search className="h-4 w-4 mr-2" />
            {consultando ? 'Consultando...' : 'Consultar Todas'}
          </Button>
        </div>
      </div>

      {/* Cards de Resumo */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Multas Novas</p>
                <p className="text-2xl font-bold text-red-600">{multasNovas.length}</p>
              </div>
              <AlertTriangle className="h-8 w-8 text-red-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Vencidas</p>
                <p className="text-2xl font-bold text-red-600">{multasVencidas.length}</p>
              </div>
              <Clock className="h-8 w-8 text-red-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Vencem em 7 dias</p>
                <p className="text-2xl font-bold text-orange-600">{multasProximoVencimento.length}</p>
              </div>
              <Calendar className="h-8 w-8 text-orange-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Valor Total</p>
                <p className="text-2xl font-bold text-blue-600">
                  R$ {totalMultas.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </p>
              </div>
              <DollarSign className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Consulta Manual */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Search className="h-5 w-5 text-blue-500" />
            <span>Consulta Manual por Placa</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex space-x-4 items-end">
            <div className="flex-1">
              <Label htmlFor="placa">Placa da Moto</Label>
              <Input
                id="placa"
                value={placaConsulta}
                onChange={(e) => setPlacaConsulta(e.target.value.toUpperCase())}
                placeholder="ABC1234"
                maxLength={7}
              />
            </div>
            <Button 
              onClick={consultarPlaca}
              disabled={consultando || !placaConsulta.trim()}
              className="bg-green-600 hover:bg-green-700"
            >
              <Search className="h-4 w-4 mr-2" />
              Consultar
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Status do Serviço */}
      <Alert className="border-green-200 bg-green-50">
        <Zap className="h-4 w-4 text-green-600" />
        <AlertDescription className="text-green-800">
          🤖 <strong>Consulta Automática ATIVA:</strong> Sistema consulta DETRAN-SP automaticamente a cada 24h para detectar novas multas.
        </AlertDescription>
      </Alert>

      {/* Multas Novas - Prioridade */}
      {multasNovas.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              <span>⚠️ Multas Novas Detectadas ({multasNovas.length})</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {multasNovas.map((multa) => (
                <Alert key={multa.id} className="border-l-4 border-l-red-500 bg-red-50">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <Badge className={getTipoColor(multa.tipo_infracao)}>
                          {getTipoText(multa.tipo_infracao)}
                        </Badge>
                        <Badge className="bg-red-100 text-red-800">NOVA</Badge>
                      </div>
                      
                      <h4 className="font-semibold">{multa.placa} - {multa.descricao_infracao}</h4>
                      
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm mt-2">
                        <div className="flex items-center space-x-1">
                          <Calendar className="h-3 w-3 text-gray-500" />
                          <span>{formatDate(multa.data_infracao)}</span>
                        </div>
                        
                        <div className="flex items-center space-x-1">
                          <MapPin className="h-3 w-3 text-gray-500" />
                          <span className="truncate">{multa.local_infracao}</span>
                        </div>
                        
                        <div className="flex items-center space-x-1">
                          <DollarSign className="h-3 w-3 text-gray-500" />
                          <span>
                            R$ {(multa.valor_desconto && temDesconto(multa) ? multa.valor_desconto : multa.valor_original).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                          </span>
                        </div>
                        
                        <div className="flex items-center space-x-1">
                          <Clock className="h-3 w-3 text-gray-500" />
                          <span>Vence: {formatDate(multa.data_vencimento)}</span>
                        </div>
                      </div>
                      
                      {multa.valor_desconto && temDesconto(multa) && (
                        <div className="mt-2 p-2 bg-green-100 rounded text-sm text-green-800">
                          💰 Desconto disponível até {formatDate(multa.data_vencimento_desconto)}: 
                          R$ {multa.valor_desconto.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                        </div>
                      )}
                    </div>
                    
                    <div className="flex space-x-2">
                      <Button 
                        size="sm"
                        className="bg-green-600 hover:bg-green-700"
                        onClick={() => marcarComoNotificada(multa.id)}
                      >
                        <CheckCircle className="h-4 w-4 mr-1" />
                        Notificar
                      </Button>
                      
                      {multa.cobranca_id && (
                        <Button 
                          size="sm"
                          variant="outline"
                          onClick={() => window.location.href = '/admin/cobrancas'}
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          Ver Cobrança
                        </Button>
                      )}
                    </div>
                  </div>
                </Alert>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Histórico de Multas */}
      <Card>
        <CardHeader>
          <CardTitle>Histórico de Multas ({multas.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {multas.length === 0 ? (
            <div className="text-center py-8">
              <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
              <p className="text-gray-600">🎉 Nenhuma multa detectada! Sua frota está em dia.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {multas.slice().reverse().map((multa) => (
                <Card key={multa.id} className="border border-gray-200">
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <div className="flex items-center space-x-2 mb-2">
                          <Badge className={getTipoColor(multa.tipo_infracao)}>
                            {getTipoText(multa.tipo_infracao)}
                          </Badge>
                          <Badge className={getStatusColor(multa.status)}>
                            {getStatusText(multa.status)}
                          </Badge>
                          {isVencida(multa.data_vencimento) && multa.status !== 'paga' && (
                            <Badge className="bg-red-100 text-red-800">VENCIDA</Badge>
                          )}
                        </div>
                        <h3 className="font-semibold">
                          {multa.placa} - {multa.descricao_infracao}
                        </h3>
                      </div>
                      
                      <div className="text-right">
                        <p className="text-lg font-bold">
                          R$ {(multa.valor_desconto && temDesconto(multa) ? multa.valor_desconto : multa.valor_original).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                        </p>
                        {multa.pontos_cnh > 0 && (
                          <p className="text-sm text-red-600">{multa.pontos_cnh} ponto(s)</p>
                        )}
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div className="flex items-center space-x-2">
                        <Calendar className="h-4 w-4 text-gray-500" />
                        <div>
                          <p className="text-gray-600">Infração</p>
                          <p className="font-medium">{formatDate(multa.data_infracao)}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <Clock className="h-4 w-4 text-gray-500" />
                        <div>
                          <p className="text-gray-600">Vencimento</p>
                          <p className="font-medium">{formatDate(multa.data_vencimento)}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <MapPin className="h-4 w-4 text-gray-500" />
                        <div>
                          <p className="text-gray-600">Local</p>
                          <p className="font-medium text-xs">{multa.local_infracao}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <User className="h-4 w-4 text-gray-500" />
                        <div>
                          <p className="text-gray-600">Auto nº</p>
                          <p className="font-medium text-xs">{multa.numero_auto}</p>
                        </div>
                      </div>
                    </div>
                    
                    {multa.velocidade_permitida && multa.velocidade_medida && (
                      <div className="mt-3 p-2 bg-red-50 rounded text-sm">
                        🚗 Velocidade: {multa.velocidade_medida}km/h (permitido: {multa.velocidade_permitida}km/h)
                      </div>
                    )}
                    
                    {multa.valor_desconto && temDesconto(multa) && (
                      <div className="mt-2 p-2 bg-green-100 rounded text-sm text-green-800">
                        💰 Desconto disponível até {formatDate(multa.data_vencimento_desconto)}: 
                        R$ {multa.valor_desconto.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};